import { BulkOperations } from '@openx/components-header/BulkOperations/BulkOperations.tsx';
import type { ActionButtonProps } from '@openx/components-header/HeaderActions/ActionButton.tsx';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DealRowActionType } from '../../constants.ts';
import type { DealActionResult, DealRowActionPayload } from '../utils/context/DealHookTypes.ts';
import { useDealsOperations } from '../utils/hooks/useDealsOperations.tsx';

export type DealsBulkOperationsPayload = DealRowActionPayload;

interface DealsBulkOperationsProps {
  selectedDeals: DealsBulkOperationsPayload[];
  onClose: () => void;
  availableActions: DealRowActionType[];
  onActionFinish?: (actionResult?: DealActionResult) => void;
}

type HeaderActionExtended = ActionButtonProps & { actionType: DealRowActionType };

export function DealsBulkOperations({
  selectedDeals,
  onClose,
  availableActions,
  onActionFinish,
}: DealsBulkOperationsProps) {
  const { t } = useTranslation();

  const { useIsAllowed } = usePermissionContext();

  const { isAllowed } = useIsAllowed();
  const { callDealAction, loadingAction } = useDealsOperations();

  const actions = useMemo(() => {
    const defaultActions: HeaderActionExtended[] = [
      {
        actionType: DealRowActionType.DELETE,
        color: 'error',
        'data-test': 'bulk-delete-button',
        disabled: !!loadingAction,
        isAllowed: isAllowed(isAllowedByCompiledAcl(ACL_TYPE.DEAL_DELETE)),
        label: t('Delete'),
        loading: loadingAction === DealRowActionType.DELETE,
        onClick: () => {
          callDealAction({
            action: DealRowActionType.DELETE,
            deals: selectedDeals.map(deal => ({ ...deal, id: deal.uid })),
            isBulkMode: true,
            onFinish: onActionFinish,
          });
        },
      },
      {
        actionType: DealRowActionType.CLONE,
        color: 'secondary',
        'data-test': 'bulk-clone-button',
        disabled: !!loadingAction,
        isAllowed: isAllowed(isAllowedByCompiledAcl(ACL_TYPE.DEAL_CREATE)),
        label: t('Clone'),
        loading: loadingAction === DealRowActionType.CLONE,
        onClick: () => {
          callDealAction({
            action: DealRowActionType.CLONE,
            deals: selectedDeals,
            isBulkMode: true,
            onFinish: onActionFinish,
          });
        },
      },
      {
        actionType: DealRowActionType.RESUME,
        color: 'secondary',
        'data-test': 'bulk-resume-button',
        disabled: !!loadingAction,
        isAllowed: isAllowed(isAllowedByCompiledAcl(ACL_TYPE.DEAL_UPDATE)),
        label: t('Resume'),
        loading: loadingAction === DealRowActionType.RESUME,
        onClick: () => {
          callDealAction({
            action: DealRowActionType.RESUME,
            deals: selectedDeals,
            isBulkMode: true,
            onFinish: onActionFinish,
          });
        },
      },
      {
        actionType: DealRowActionType.PAUSE,
        color: 'secondary',
        'data-test': 'bulk-pause-button',
        disabled: !!loadingAction,
        isAllowed: isAllowed(isAllowedByCompiledAcl(ACL_TYPE.DEAL_UPDATE)),
        label: t('Pause'),
        loading: loadingAction === DealRowActionType.PAUSE,
        onClick: () => {
          callDealAction({
            action: DealRowActionType.PAUSE,
            deals: selectedDeals,
            isBulkMode: true,
            onFinish: onActionFinish,
          });
        },
      },
      {
        actionType: DealRowActionType.DOWNLOAD,
        color: 'secondary',
        'data-test': 'bulk-download-button',
        disabled: !!loadingAction,
        isAllowed: true,
        label: t('Download'),
        loading: loadingAction === DealRowActionType.DOWNLOAD,
        onClick: () => {
          callDealAction({
            action: DealRowActionType.DOWNLOAD,
            deals: selectedDeals,
            isBulkMode: true,
            onFinish: onActionFinish,
          });
        },
      },
    ];

    return defaultActions
      .filter(item => availableActions.includes(item.actionType))
      .map(({ actionType: _, ...rest }) => rest);
  }, [availableActions, callDealAction, isAllowed, loadingAction, onActionFinish, selectedDeals, t]);

  return <BulkOperations selectedItems={selectedDeals} actions={actions} onClose={onClose} />;
}
